import React from "react";
import {
    Actions,
    Box,
    Button,
    IconAdd,
    IconDelete,
    IconEdit,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TooltipRenderer,
} from "braid-design-system";
import { BomIngredientRow, computeIngredientTotalQty, computeTotalBomCost } from "../../types/bom";

interface BomIngredientsTableProps {
    ingredients: BomIngredientRow[];
    canEdit: boolean;
    onAddIngredient: () => void;
    onEditIngredient: (row: BomIngredientRow) => void;
    onDeleteIngredient: (rowId: string) => void;
}

const formatNumber = (value: number, decimals = 3): string => value.toFixed(decimals);

const BomIngredientsTable: React.FC<BomIngredientsTableProps> = ({
    ingredients,
    canEdit,
    onAddIngredient,
    onEditIngredient,
    onDeleteIngredient,
}) => {
    const totalCost = computeTotalBomCost(ingredients);

    return (
        <Stack space="medium">
            <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                <Text weight="strong" size="large">
                    Ingredients
                </Text>
                {canEdit ? (
                    <Actions>
                        <Button variant="ghost" tone="brandAccent" icon={<IconAdd />} onClick={onAddIngredient}>
                            Add Ingredient
                        </Button>
                    </Actions>
                ) : null}
            </Box>

            <Table label="BOM Ingredients Table">
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>
                            <Text>#</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>Ingredient Code</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>Ingredient Name</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>Qty per 1</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>UOM</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>Wastage %</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>Total Qty</Text>
                        </TableHeaderCell>
                        {canEdit ? (
                            <TableHeaderCell align="right">
                                <Text>Action</Text>
                            </TableHeaderCell>
                        ) : null}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {ingredients.length === 0 ? (
                        <TableRow>
                            <TableCell colspan={canEdit ? 8 : 7} align="center">
                                <Text tone="secondary">No ingredients mapped yet. Click "Add Ingredient" to build this BOM.</Text>
                            </TableCell>
                        </TableRow>
                    ) : (
                        ingredients.map((row, index) => (
                            <TableRow key={row.row_id}>
                                <TableCell>
                                    <Text>{index + 1}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text weight="medium">{row.ingredient_code}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{row.ingredient_name}</Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Text>{formatNumber(row.qty_per_1)}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{row.uom}</Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Text>{formatNumber(row.wastage_pct, 2)}</Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Text>{formatNumber(computeIngredientTotalQty(row))}</Text>
                                </TableCell>
                                {canEdit ? (
                                    <TableCell align="right">
                                        <Inline space="small">
                                            <TooltipRenderer tooltip={<Text>Edit Ingredient</Text>}>
                                                {({ triggerProps }) => (
                                                    <Box onClick={() => onEditIngredient(row)} cursor="pointer" {...triggerProps}>
                                                        <IconEdit tone="info" />
                                                    </Box>
                                                )}
                                            </TooltipRenderer>
                                            <TooltipRenderer tooltip={<Text>Delete Ingredient</Text>}>
                                                {({ triggerProps }) => (
                                                    <Box onClick={() => onDeleteIngredient(row.row_id)} cursor="pointer" {...triggerProps}>
                                                        <IconDelete tone="critical" />
                                                    </Box>
                                                )}
                                            </TooltipRenderer>
                                        </Inline>
                                    </TableCell>
                                ) : null}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            <Box
                background="neutralLight"
                borderRadius="standard"
                padding="medium"
                display="flex"
                justifyContent="spaceBetween"
                alignItems="center"
                style={{ flexWrap: "wrap", gap: 12 }}
            >
                <Stack space="xsmall">
                    <Text tone="secondary" size="small">
                        Total Ingredient Cost (per 1 unit)
                    </Text>
                    <Text weight="strong" size="large">
                        {`Rs. ${totalCost.toFixed(2)}`}
                    </Text>
                </Stack>
                <Button variant="soft" tone="neutral">
                    View Cost Breakdown
                </Button>
            </Box>
        </Stack>
    );
};

export default BomIngredientsTable;
